"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Check, X, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { SubscriptionPlan } from "@/types";
import { Subscription } from "@/types/subscription.types";

interface SubscriptionPlansGridProps {
  plans: SubscriptionPlan[];
  subscriptions?: Subscription[];
  isLoading?: boolean;
  onEdit?: (subscription: Subscription) => void;
  onDelete?: (subscription: Subscription) => void;
}

// Helper function to check if a feature value is truthy
const isFeatureIncluded = (value: string | boolean | number, type: string): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const lowerValue = value.toLowerCase();
    if (type.toLowerCase() === "boolean") {
      return lowerValue === "true" || lowerValue === "1";
    }
    // For string/number types, if it's a number string, check if > 0
    const numValue = Number(value);
    if (!isNaN(numValue)) {
      return numValue > 0;
    }
    return lowerValue === "true" || lowerValue === "1";
  }
  if (typeof value === "number") return value > 0;
  return false;
};

// Helper function to format feature value for display
const formatFeatureValue = (value: string | boolean | number, type: string): string => {
  if (type.toLowerCase() === "boolean") return "";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") {
    // Don't show "true" or "false" strings
    const lowerValue = value.toLowerCase();
    if (lowerValue === "true" || lowerValue === "false") return "";
    // Show numeric strings and other values
    return value;
  }
  return "";
};

export function SubscriptionPlansGrid({ 
  plans, 
  subscriptions = [], 
  isLoading = false,
  onEdit,
  onDelete,
}: SubscriptionPlansGridProps) {
  if (isLoading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Loading subscriptions...
      </div>
    );
  }

  const allSubscriptions = subscriptions.filter((sub) => !sub.isDeleted);

  return (
    <div className="space-y-6">
      {allSubscriptions.length > 0 && (
        <div>
          <div className="grid gap-6 md:grid-cols-3">
            {allSubscriptions.map((sub) => (
              <Card
                key={sub.id}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle>{sub.name}</CardTitle>
                      <p className="text-2xl font-bold mt-2">
                        {formatCurrency(sub.price)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {sub.duration} days
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="default">Active</Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreVertical className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => onEdit?.(sub)}
                            className="cursor-pointer"
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit Plan
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => onDelete?.(sub)}
                            variant="destructive"
                            className="cursor-pointer"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Plan
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {sub.features.map((feature, index) => {
                      const included = isFeatureIncluded(feature.value, feature.type);
                      const valueDisplay = formatFeatureValue(feature.value, feature.type);
                      return (
                        <li
                          key={feature.id || index}
                          className="text-sm flex items-start gap-2"
                        >
                          {included ? (
                            <Check className="h-4 w-4 text-green-500 mt-0.5" />
                          ) : (
                            <X className="h-4 w-4 text-muted-foreground mt-0.5" />
                          )}
                          <span
                            className={
                              !included ? "text-muted-foreground" : ""
                            }
                          >
                            {feature.name}
                            {valueDisplay && ` (${valueDisplay})`}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {!isLoading && plans.length === 0 && allSubscriptions.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          No subscription plans available
        </div>
      )}
    </div>
  );
}

